// Copyright (c) 2022-23, iPivot, Corp.
'use strict'

import axios from 'axios'
import { useEffect, useState } from 'react'

import { availableThemes, resolveTheme } from '@ipivot/themes-browser'

/*
 * Resolve the theme this page should render in.
 *
 * Two very different situations share this hook:
 *
 *   /login  has no session. It cannot read /settings, and it cannot use the websocket
 *           RPC path either, because that handshake requires a session. Its appearance
 *           comes from one unauthenticated endpoint, /themes/login.
 *   else    the signed-in app. GET /settings returns the user's own settings deep-merged
 *           over the system defaults, so `themeVariant` already carries the user's choice
 *           where they made one and the system default where they did not.
 *
 * Both fetches are independently fault tolerant: a failure leaves that input empty rather
 * than losing the other one, and resolveTheme always lands on a real theme.
 */
export const useTheme = () => {
    const [theme, setTheme] = useState({})
    const isLoginPage = typeof window !== 'undefined' && window.location?.pathname?.startsWith('/login')

    useEffect(() => {
        const fetchThemes = async () => {
            const preference = isLoginPage
                ? axios.get('/themes/login').then(res => ({ variant: res.data.variant }))
                : axios.get('/settings').then(res => ({
                      // An explicit theme pick wins over the dark/light variant.
                      themeId: res.data.selectedTheme,
                      variant: res.data.themeVariant,
                  }))

            const [themes, pref] = await Promise.all([
                availableThemes().catch(() => []),
                preference.catch(() => ({})),
            ])

            const resolved = resolveTheme(themes, pref)

            /* Publish the decision on <html> so it is observable from outside React --
             * the UI tests assert on it rather than on a computed colour, which would be
             * pinned to the current palette and break on any restyle. It is set here
             * rather than in a component so every entry point (login, prod, maint) gets
             * it from the one place that actually makes the choice.
             */
            if (typeof document !== 'undefined') {
                document.documentElement.dataset.themeVariant = resolved.variant || ''
            }

            setTheme(resolved.theme)
        }

        fetchThemes()
    }, [isLoginPage])

    return theme
}
